import React, { useEffect, useState } from 'react';
import { sendContactEmail } from '../services/emailService';

const getAsuntoFromHash = () => {
  const raw = window.location.hash.slice(1) || '';
  const query = raw.includes('?') ? raw.split('?')[1] : '';
  if (!query) return '';
  try {
    return new URLSearchParams(query).get('asunto')?.trim() || '';
  } catch {
    return '';
  }
};

const Contact = () => {
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    correo: '',
    empresa: '',
    asunto: getAsuntoFromHash(),
    pregunta: '',
    company_fax: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('');

  useEffect(() => {
    const syncAsuntoFromHash = () => {
      const asunto = getAsuntoFromHash();
      if (!asunto) return;
      setFormData((prev) =>
        prev.asunto === asunto ? prev : { ...prev, asunto }
      );
    };

    syncAsuntoFromHash();
    window.addEventListener('hashchange', syncAsuntoFromHash);
    return () => window.removeEventListener('hashchange', syncAsuntoFromHash);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      telefono: '',
      correo: '',
      empresa: '',
      asunto: '',
      pregunta: '',
      company_fax: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setStatusMessage('');
    setStatusType('');

    // Honeypot: si un bot llena este campo oculto, fingimos éxito y no enviamos.
    if (formData.company_fax.trim()) {
      setStatusType('success');
      setStatusMessage(
        'Gracias por tu mensaje. Nos pondremos en contacto contigo a la brevedad.'
      );
      resetForm();
      setIsSending(false);
      return;
    }

    try {
      await sendContactEmail({
        from_name: formData.nombre,
        from_email: formData.correo,
        phone: formData.telefono || 'No proporcionado',
        company: formData.empresa || 'No proporcionada',
        subject: formData.asunto,
        message: formData.pregunta,
      });

      setStatusType('success');
      setStatusMessage(
        'Gracias por tu mensaje. Nos pondremos en contacto contigo a la brevedad.'
      );
      resetForm();
    } catch (error) {
      setStatusType('error');
      setStatusMessage(
        'No se pudo enviar el mensaje por ahora. Intenta de nuevo en unos minutos.'
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="contact-page" id="contacto">
      <div className="contact-page-container">
        <div className="contact-form-wrapper">
          <h1 className="contact-page-title">
            ¿Tienes un proyecto en mente o necesitas ayuda para elegir el mobiliario ideal?
          </h1>
          <p className="contact-page-intro">
            En Verissa estamos para apoyarte. Completa el siguiente formulario y nuestro equipo se pondrá
            en contacto contigo a la brevedad para brindarte asesoría personalizada, resolver tus dudas
            o preparar una cotización.
          </p>
          <form className="contact-form" onSubmit={handleSubmit} autoComplete="on">
            <div className="contact-honeypot" aria-hidden="true">
              <label htmlFor="company_fax">Fax</label>
              <input
                id="company_fax"
                name="company_fax"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={formData.company_fax}
                onChange={handleChange}
              />
            </div>
            <div className="form-row">
              <label htmlFor="nombre">Nombre *</label>
              <input
                id="nombre"
                name="nombre"
                type="text"
                required
                value={formData.nombre}
                onChange={handleChange}
                placeholder="Tu nombre"
              />
            </div>
            <div className="form-row">
              <label htmlFor="telefono">Número de teléfono</label>
              <input
                id="telefono"
                name="telefono"
                type="tel"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="+52"
              />
            </div>
            <div className="form-row">
              <label htmlFor="correo">Correo electrónico *</label>
              <input
                id="correo"
                name="correo"
                type="email"
                required
                value={formData.correo}
                onChange={handleChange}
                placeholder="correo@ejemplo.com"
              />
            </div>
            <div className="form-row">
              <label htmlFor="empresa">Empresa - Opcional</label>
              <input
                id="empresa"
                name="empresa"
                type="text"
                value={formData.empresa}
                onChange={handleChange}
                placeholder="Nombre de tu empresa"
              />
            </div>
            <div className="form-row">
              <label htmlFor="asunto">Asunto *</label>
              <input
                id="asunto"
                name="asunto"
                type="text"
                required
                value={formData.asunto}
                onChange={handleChange}
                placeholder="Asunto del mensaje"
              />
            </div>
            <div className="form-row">
              <label htmlFor="pregunta">Pregunta *</label>
              <textarea
                id="pregunta"
                name="pregunta"
                required
                rows={5}
                value={formData.pregunta}
                onChange={handleChange}
                placeholder="Escribe tu mensaje o consulta..."
              />
            </div>
            <button type="submit" className="contact-submit-btn" disabled={isSending}>
              {isSending ? 'Enviando...' : 'Enviar'}
            </button>
            {statusMessage && (
              <p className={`contact-form-status ${statusType}`}>
                {statusMessage}
              </p>
            )}
          </form>
        </div>
        <div className="contact-info-sidebar">
          <h3>Showroom Obispado | Nuevo León, México</h3>
          <p className="contact-address">
            Lic. Jose Benitez 2460 Local 2, Obispado N.L.
          </p>
          <a href="tel:+528113587217" className="contact-info-item">+52 (81) 1358-7217</a>
          <a href="tel:+528112312523" className="contact-info-item">+52 (81) 1231-2523</a>
          <a href="mailto:contacto@verissa.mx" className="contact-info-item">contacto@verissa.mx</a>
          <div className="contact-map-wrapper">
            <iframe
              title="Ubicación Verissa Showroom Obispado"
              src="https://www.google.com/maps?q=Lic.+Jose+Benitez+2460,+Obispado,+Monterrey,+N.L.&output=embed"
              className="contact-map-iframe"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
